/*!
 * Stripped fork of https://github.com/Starcounter-Jack/JSON-Patch
 * Only the diff algorithm — observer system and invertible mode removed.
 * (c) 2017-2021 Joachim Wester — MIT license
 */

import { _deepClone, type Operation } from './core'

// Object.keys on arrays enumerates the prototype chain for sparse slots.
// Hand-rolled index keys avoid that and pre-allocate the result array.
function _objectKeys(obj: any): string[] {
  if (Array.isArray(obj)) {
    const keys = new Array(obj.length)
    for (let k = 0; k < keys.length; k++) {
      keys[k] = '' + k
    }
    return keys
  }
  return Object.keys(obj)
}

// RFC 6901 §3: '~' → '~0', '/' → '~1'. Fast path skips replace when neither is present.
function escapePathComponent(path: string): string {
  if (path.indexOf('/') === -1 && path.indexOf('~') === -1) return path
  return path.replace(/~/g, '~0').replace(/\//g, '~1')
}

function _generate(
  mirror: any,
  obj: any,
  patches: Operation[],
  path: string,
): void {
  // Reference equality — skip entire subtree if unchanged.
  if (obj === mirror) {
    return
  }

  const newKeys = _objectKeys(obj)
  const oldKeys = _objectKeys(mirror)
  let deleted = false

  // Walk old keys to find removes and replaces.
  for (let t = oldKeys.length - 1; t >= 0; t--) {
    const key = oldKeys[t]
    const oldVal = mirror[key]
    const keyPath = path + '/' + escapePathComponent(key)
    if (Object.hasOwn(obj, key)) {
      const newVal = obj[key]
      if (
        typeof oldVal == 'object' &&
        oldVal != null &&
        typeof newVal == 'object' &&
        newVal != null &&
        Array.isArray(oldVal) === Array.isArray(newVal)
      ) {
        _generate(oldVal, newVal, patches, keyPath)
      } else {
        if (oldVal != newVal) { // loose: null == undefined
          // Clone so patch values are independent of the source tree.
          patches.push({ op: 'replace', path: keyPath, value: _deepClone(newVal) })
        }
      }
    } else if (Array.isArray(mirror) === Array.isArray(obj)) {
      patches.push({ op: 'remove', path: keyPath })
      deleted = true
    } else {
      // Type changed (e.g. object → array) — replace the whole subtree.
      patches.push({ op: 'replace', path: path, value: obj })
    }
  }

  // If nothing was deleted and key count is unchanged, no adds are possible.
  if (!deleted && newKeys.length == oldKeys.length) {
    return
  }

  // Walk new keys to find adds.
  for (let t = 0; t < newKeys.length; t++) {
    const key = newKeys[t]
    if (!Object.hasOwn(mirror, key)) {
      patches.push({
        op: 'add',
        path: path + '/' + escapePathComponent(key),
        value: _deepClone(obj[key]),
      })
    }
  }
}

/**
 * Diff two JSON-safe values and return an RFC 6902 patch array.
 */
export function compare(
  tree1: object | Array<any>,
  tree2: object | Array<any>,
): Operation[] {
  const patches: Operation[] = []
  _generate(tree1, tree2, patches, '')
  return patches
}
